import { useEffect, useMemo, useState } from "react";

import {
  ArrowButton,
  Select,
  TableMetadata,
  TableStateWithFilters,
  useTable,
  Label,
  ColumnSortEnum,
} from "@king-ict/eupisi-ui/components";
import { PagedResult } from "@king-ict/eupisi-ui/models";
import {
  codeBookToSelectData,
  hrDate,
  nameof,
} from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { searchCountyCommittee } from "../../api/Committee/CountyCommitteeApi";
import {
  ContentArea,
  ToolbarTop,
  SearchCard,
  ListTable,
} from "../../components";
import { useEnumCodeBooks } from "../../hooks";
import { useAuth, useAuthCounties } from "../../hooks/auth";
import {
  CountyCommittee,
  CommitteeSearch,
  CommitteeSort,
  CommitteeStatus,
  CommitteeTypeEnum,
} from "../../models/Committee";
import { ImportantDateEventEnum } from "../../models/ImportantDate/ImportantDateEventEnum";
import { ClaimType } from "../../models/User";
import { useGetSchoolYearCurrentQuery } from "../../queries/codeBookQuery";
import { countyCommitteeEditUrl } from "../../routes/routes";
import { FormatUtils, TableUtils } from "../../utils";

const storageKey = "CountyCommitteeSearchTableState";

export function CountyCommitteeList() {
  const { t } = useTranslation();
  const history = useHistory();
  const auth = useAuth();
  const counties = useAuthCounties();

  //INIT TABLE STATE WITH FILTERS - could be temporary stored in local session storage
  const INITIAL_TABLE_STATE = useMemo<TableStateWithFilters<CommitteeSearch>>(
    () =>
      TableUtils.CreateInitialTableState<CommitteeSearch>(
        storageKey,
        TableUtils.tableDefaultState(CommitteeSort.Name, ColumnSortEnum.DESC)
      ),
    []
  );
  const defaultCountyId =
    auth.currentRole?.claimType === ClaimType.County &&
    (auth.currentRole?.locations?.length ?? 0) > 0
      ? auth!.currentRole!.locations![0]
      : undefined;

  //INIT SEARCH FILTER
  const CREATE_INITIAL_COMMITTEE_SEARCH = useMemo(
    () => (): CommitteeSearch => ({
      countyId: INITIAL_TABLE_STATE.countyId
        ? INITIAL_TABLE_STATE.countyId
        : defaultCountyId,
      committeeTypeId: CommitteeTypeEnum.County,
    }),
    [INITIAL_TABLE_STATE.countyId, defaultCountyId]
  );
  //TABLE DATA
  const defaultTableData = TableUtils.tableDefaultData<CountyCommittee>();
  const [tableData, setTableData] =
    useState<PagedResult<CountyCommittee>>(defaultTableData);
  //SET SEARCH FILTERS
  const [expertCommissionSearch, setExpertCommissionSearch] =
    useState<CommitteeSearch>(CREATE_INITIAL_COMMITTEE_SEARCH);
  const [tableSearch, setTableSearch] = useState<CommitteeSearch>(
    CREATE_INITIAL_COMMITTEE_SEARCH
  );
  const [filterChanged, setFilterChanged] = useState(false);
  const [clearFilter, setClearFilter] = useState(true);
  //TABLE METADATA
  const tableMetadata: TableMetadata<CountyCommittee> = {
    rowKey: "id",
    columns: [
      {
        columnKey: "county",
        header: t("common.expertCommission.county"),
        accessor: (item: CountyCommittee) => {
          return (
            <div>{counties.find((c) => c.id === item.countyId)?.name}</div>
          );
        },
        sortBy: CommitteeSort.Name,
      },
      {
        columnKey: "dateFromDateTo",
        header: t("common.expertCommission.dateFromDateTo"),
        accessor: (item: CountyCommittee) => {
          return (
            <div>
              {item.dateFrom ? hrDate(new Date(item.dateFrom)) : ""} -{" "}
              {item.dateTo ? hrDate(new Date(item.dateTo)) : ""}
            </div>
          );
        },
        sortBy: CommitteeSort.DateFrom,
      },
      {
        columnKey: "status",
        header: t("common.status"),
        accessor: (item: CountyCommittee) => {
          return (
            <Label
              color={FormatUtils.getCommitteeStatusColor(item.statusId)}
              className="text-center"
              outline={false}
              fullWidth={false}
            >
              {statuses.find((v) => v.id === item.statusId)?.name}
            </Label>
          );
        },
        sortBy: CommitteeSort.Status,
      },
      {
        columnKey: "details",
        header: "",
        accessor: (item: CountyCommittee) => {
          return (
            <div className={"text-right"}>
              <ArrowButton
                ariaLabel={t("common.details")}
                onClick={() => handleDetailsClicked(item.id)}
              />
            </div>
          );
        },
      },
    ],
    rowToolTip: () => undefined,
  };
  //FILTER DATA
  const [tableProps, tableState, setFilters] = useTable<CommitteeSearch>({
    metadata: tableMetadata,
    data: tableData!.results,
    pagination: true,
    paginationPage: INITIAL_TABLE_STATE.page,
    paginationPageSize: INITIAL_TABLE_STATE.pageSize as any,
    totalItems: tableData!.rowCount,
    defaultSort: {
      order: INITIAL_TABLE_STATE.order,
      sortBy: INITIAL_TABLE_STATE.sortBy,
    },
  });
  //FILTER DATA
  const { data: schoolYear } = useGetSchoolYearCurrentQuery();
  const { committeeStatusCodeBook: statuses } = useEnumCodeBooks();
  const [isEditable, setIsEditable] = useState(false);

  useEffect(() => {
    (async () => {
      if (schoolYear) {
        const importantDate = schoolYear.importantDates.find(
          (val) =>
            val.eventId === ImportantDateEventEnum.InstitutionAdministration
        );
        if (importantDate) {
          const exactlyNow = new Date(Date.now());
          const dateFrom = new Date(importantDate.dateTimeFrom);
          const dateTo = new Date(importantDate.dateTimeTo);
          if (dateFrom <= exactlyNow && dateTo >= exactlyNow) {
            setIsEditable(true);
          }
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolYear]);

  useEffect(() => {
    if (clearFilter) {
      setClearFilter(false);
      setFilterChanged(false);
    } else setFilterChanged(true);
    TableUtils.RemoveSessionStorageItem(storageKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expertCommissionSearch]);

  useEffect(() => {
    (async () => {
      const request = TableUtils.GetSearchRequest<CommitteeSearch>(
        tableSearch,
        tableState
      );
      const itemList = await searchCountyCommittee(request);
      if (itemList !== null && itemList.results.length > 0) {
        setTableData(itemList);
      } else {
        setTableData(defaultTableData);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState]);

  const handleNewExpertCommissionClicked = () => {
    if (expertCommissionSearch.countyId !== undefined) {
      TableUtils.SetSessionStorageItem<CommitteeSearch>(
        storageKey,
        tableSearch,
        tableState
      );
      history.push(countyCommitteeEditUrl(0, expertCommissionSearch.countyId));
    }
  };
  const handleDetailsClicked = (id?: number) => {
    if (id) {
      TableUtils.SetSessionStorageItem<CommitteeSearch>(
        storageKey,
        tableSearch,
        tableState
      );
      history.push(countyCommitteeEditUrl(id!));
    }
  };
  const search = () => {
    setFilters({ ...tableSearch });
  };

  const handleCountyChange = (e: any) => {
    const county = Number.parseInt(e.target.value);
    if (!isNaN(county)) {
      const newExpertCommissionSearch: CommitteeSearch = {
        ...expertCommissionSearch,
        countyId: county,
      };
      setExpertCommissionSearch(newExpertCommissionSearch);
    }
  };

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={
          expertCommissionSearch.countyId !== undefined &&
          !filterChanged &&
          tableData.results?.find(
            (val) =>
              val.statusId === CommitteeStatus.NotDefined ||
              val.statusId === CommitteeStatus.ConfirmationRequired
          ) === undefined &&
          isEditable
            ? "show"
            : "disable"
        }
        showBack={"hide"}
        onClickAdd={handleNewExpertCommissionClicked}
        pageTitle={t("common.expertCommission.countyPageName")}
      />
      <SearchCard
        onClickSearch={() => {
          setTableSearch(expertCommissionSearch);
          setFilterChanged(false);
          search();
        }}
        onClickClearSearch={() => {
          setClearFilter(true);
          setExpertCommissionSearch({
            countyId: defaultCountyId,
            committeeTypeId: CommitteeTypeEnum.County,
          });
          setTableSearch({
            countyId: defaultCountyId,
            committeeTypeId: CommitteeTypeEnum.County,
          });
          search();
        }}
        isFilterChanged={filterChanged}
      >
        <Row>
          <Col md={6}>
            <Select
              label={t("common.schoolCandidateModel.countyList")}
              name={nameof<typeof expertCommissionSearch>("countyId")}
              value={expertCommissionSearch.countyId}
              onChange={handleCountyChange}
              selectData={codeBookToSelectData(counties)}
            />
          </Col>
        </Row>
      </SearchCard>
      <ListTable tableProps={tableProps} />
    </ContentArea>
  );
}
